import { differenceInMinutes, format, isSameDay, parseISO, subDays } from 'date-fns';

const intFormat = new Intl.NumberFormat('en-GB');

export type Delta = { text: string; tone: 'up' | 'dn' | 'muted' };

export function fmtInt(n: number | null | undefined): string {
  return n === null || n === undefined ? '–' : intFormat.format(Math.round(n));
}

export function fmtMoney(n: number | null | undefined): string {
  if (n === null || n === undefined) return '–';
  if (Math.abs(n) >= 100) return `$${intFormat.format(Math.round(n))}`;
  return `$${n.toFixed(2)}`;
}

export function fmtPct(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '–';
  const pct = rate * 100;
  return `${pct > 0 && pct < 10 ? pct.toFixed(1) : Math.round(pct)}%`;
}

export function fmtSeconds(s: number | null | undefined): string {
  if (s === null || s === undefined) return '–';
  const whole = Math.round(s);
  if (whole < 60) return `${whole}s`;
  return `${Math.floor(whole / 60)}m ${whole % 60}s`;
}

export function fmtTokens(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

function safeFormat(iso: string, pattern: string): string {
  const date = parseISO(iso);
  return Number.isNaN(date.getTime()) ? iso : format(date, pattern);
}
export const fmtDay = (iso: string) => safeFormat(iso, 'd MMM');
export const fmtTime = (iso: string) => safeFormat(iso, 'HH:mm');
export const fmtDateTime = (iso: string) => safeFormat(iso, 'd MMM, HH:mm');

export function fmtAgo(iso: string | null, now: string): string {
  if (!iso) return '–';
  const minutes = Math.max(0, differenceInMinutes(parseISO(now), parseISO(iso)));
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h`;
  return `${Math.floor(minutes / 1440)}d`;
}

export function dayHeading(iso: string, now: string): string {
  const day = parseISO(iso);
  const today = parseISO(now);
  const label = format(day, 'EEE d MMM');
  if (isSameDay(day, today)) return `Today · ${label}`;
  if (isSameDay(day, subDays(today, 1))) return `Yesterday · ${label}`;
  return label;
}

export function isWeekend(day: string): boolean {
  const weekday = parseISO(day).getDay();
  return weekday === 0 || weekday === 6;
}

export function initialsOf(name: string | null | undefined): string {
  const parts = (name ?? '?').split(/[\s.@_-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts[1]?.[0] ?? '')).toUpperCase();
}

export function countDelta(cur: number | null, prev: number | null, higherIsGood = true): Delta | null {
  if (cur === null || prev === null || prev === 0) return null;
  const change = Math.round(((cur - prev) / prev) * 100);
  if (change === 0) return { text: '0%', tone: 'muted' };
  return { text: `${change > 0 ? '▲' : '▼'} ${Math.abs(change)}%`, tone: change > 0 === higherIsGood ? 'up' : 'dn' };
}

export function rateDelta(cur: number | null, prev: number | null, higherIsGood = false): Delta | null {
  if (cur === null || prev === null) return null;
  const points = (cur - prev) * 100;
  if (Math.abs(points) < 0.05) return { text: '0 pt', tone: 'muted' };
  return {
    text: `${points > 0 ? '▲' : '▼'} ${Math.abs(points).toFixed(1)} pt`,
    tone: points > 0 === higherIsGood ? 'up' : 'dn',
  };
}
